import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PopoverRef } from '../../../../@vex/components/popover/popover-ref';
import { TenantUnitDetail } from '@eadsysteme/ewsrealestateclient-v2';

export interface TenantUnitSelectPanelData {
	units: TenantUnitDetail[];
	activeId: string | undefined;
	onSelect: (unit: TenantUnitDetail) => void;
	onPanelEnter: () => void;
	onPanelLeave: () => void;
}

export interface SubRole {
	label: string;
	name: string;
}

export interface UnitDisplayInfo {
	primaryName: string;
	primaryRoleLabel: string;
	isVacancy: boolean;
	subRoles: SubRole[];
}

@Component({
	selector: 'ara-tenant-unit-select-panel',
	imports: [TranslocoModule],
	templateUrl: './tenant-unit-select-panel.component.html',
	styleUrls: ['./tenant-unit-select-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantUnitSelectPanelComponent {
	private readonly popoverRef = inject<PopoverRef<TenantUnitSelectPanelData>>(PopoverRef);
	private readonly translocoService = inject(TranslocoService);

	readonly units: TenantUnitDetail[] = this.popoverRef.data.units;
	readonly activeId: string | undefined = this.popoverRef.data.activeId;
	private readonly onSelect = this.popoverRef.data.onSelect;
	readonly onPanelEnter = this.popoverRef.data.onPanelEnter;
	readonly onPanelLeave = this.popoverRef.data.onPanelLeave;

	readonly displayInfoMap = new Map<string, UnitDisplayInfo>(this.units.map((unit) => [unit.id, this.buildDisplayInfo(unit)]));

	private buildDisplayInfo(unit: TenantUnitDetail): UnitDisplayInfo {
		const resident = this.isCurrent(unit.currentResident) ? unit.currentResident : undefined;
		const tenant = this.isCurrent(unit.currentTenant) ? unit.currentTenant : undefined;
		const owner = this.isCurrent(unit.currentOwner) ? unit.currentOwner : undefined;

		const subRoles: SubRole[] = [];

		if (owner) {
			subRoles.push({ label: this.getRoleTypeLabel('OWNER'), name: this.composeName(owner) });
		}
		if (tenant) {
			subRoles.push({ label: this.getRoleTypeLabel('TENANT'), name: this.composeName(tenant) });
		}

		if (resident) {
			return {
				primaryName: this.composeName(resident),
				primaryRoleLabel: this.getRoleTypeLabel('RESIDENT'),
				isVacancy: false,
				subRoles,
			};
		}

		return {
			primaryName: this.getRoleTypeLabel('VACANCY'),
			primaryRoleLabel: '',
			isVacancy: true,
			subRoles,
		};
	}

	private isCurrent(person: { validTo?: string } | undefined): boolean {
		if (!person) return false;
		return !person.validTo || person.validTo.startsWith('9999');
	}

	select(unit: TenantUnitDetail): void {
		this.onSelect(unit);
	}

	private composeName(person: { given?: string; family?: string; fullName?: string }): string {
		if (!person?.fullName && !person?.family) return '-';
		if (person.fullName && !person.family) return person.fullName;
		return `${person.given ?? ''} ${person.family ?? ''}`.trim() || '-';
	}

	private getRoleTypeLabel(roleType: string): string {
		const key = `tenantunitDetails.rolesView.roleTypes.${roleType}`;
		const label = this.translocoService.translate(key);
		return label === key ? roleType : label;
	}
}
